// src/dashboard/pages/teacher/Objectifs/components/ObjectifsDataTable.jsx

import React from "react";
import { FaCheck, FaEdit, FaTrash } from "react-icons/fa";

import {
  clampProgress,
  formatDateFr,
  getObjectifAdherent,
  getObjectifAdherentName,
  getObjectifGroups,
  OBJECTIF_TYPE_LABELS,
} from "../utils/objectifsDashboardUtils";

function Pill({ children, tone = "slate" }) {
  const tones = {
    slate:
      "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700",
    emerald:
      "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
    rose: "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20",
    indigo:
      "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20",
    amber:
      "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
  };

  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${
        tones[tone] || tones.slate
      }`}
    >
      {children}
    </span>
  );
}

function ProgressBar({ value }) {
  const progress = clampProgress(value);

  return (
    <div className="w-full">
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
        <div
          className="h-2 rounded-full bg-gradient-to-r from-emerald-500 via-indigo-500 to-[#4CA82F] transition-all"
          style={{ width: `${progress}%` }}
        />
      </div>

      <p className="mt-1 text-xs font-bold text-slate-600 dark:text-slate-300">
        {progress}%
      </p>
    </div>
  );
}

export default function ObjectifsDataTable({
  rows = [],
  onEdit,
  onDelete,
  onToggleDone,
  onChangeProgress,
  deletingId,
  updatingProgressId,
}) {
  return (
    <div className="hidden md:block">
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900/60">
        <table className="min-w-[1180px] w-full">
          <thead className="sticky top-0 z-20">
            <tr className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600 dark:bg-slate-900 dark:text-slate-300">
              <th className="px-4 py-3 text-left font-bold">Adhérent</th>
              <th className="px-4 py-3 text-left font-bold">Groupes</th>
              <th className="px-4 py-3 text-left font-bold">Objectif</th>
              <th className="px-4 py-3 text-left font-bold">Type</th>
              <th className="px-4 py-3 text-left font-bold">Saison</th>
              <th className="px-4 py-3 text-left font-bold">Échéance</th>
              <th className="px-4 py-3 text-left font-bold">Progression</th>
              <th className="px-4 py-3 text-left font-bold">Statut</th>
              <th className="sticky right-0 z-30 w-36 bg-slate-50 px-4 py-3 text-right font-bold dark:bg-slate-900">
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
            {rows.map((row) => {
              const adherent = getObjectifAdherent(row);
              const groups = getObjectifGroups(row);
              const progress = clampProgress(row.progress);

              return (
                <tr
                  key={row.id}
                  className="transition hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
                >
                  <td className="px-4 py-3">
                    <div className="font-bold text-slate-900 dark:text-white">
                      {getObjectifAdherentName(row)}
                    </div>

                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                      #{row.adherent_id || adherent?.id || "—"}
                    </p>
                  </td>

                  <td className="px-4 py-3">
                    {groups.length > 0 ? (
                      <div className="flex max-w-[220px] flex-wrap gap-1.5">
                        {groups.map((group) => (
                          <Pill key={group.id || group.nom}>{group.nom}</Pill>
                        ))}
                      </div>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    <div className="max-w-[360px]">
                      <p className="font-bold text-slate-900 dark:text-white">
                        {row.title || "—"}
                      </p>

                      {row.description ? (
                        <p className="mt-1 line-clamp-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
                          {row.description}
                        </p>
                      ) : null}
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    {row.type ? (
                      <Pill tone="indigo">
                        {OBJECTIF_TYPE_LABELS[row.type] || row.type}
                      </Pill>
                    ) : (
                      <Pill>—</Pill>
                    )}
                  </td>

                  <td className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                    {row.saison || "—"}
                  </td>

                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                    {formatDateFr(row.due_date)}
                  </td>

                  <td className="px-4 py-3">
                    <div className="w-44">
                      <ProgressBar value={progress} />

                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={progress}
                        disabled={updatingProgressId === row.id}
                        onChange={(event) =>
                          onChangeProgress(row, Number(event.target.value))
                        }
                        className="mt-2 w-full accent-[#013E8E]"
                      />
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    {row.is_done ? (
                      <Pill tone="emerald">Terminé</Pill>
                    ) : (
                      <Pill tone="amber">En cours</Pill>
                    )}
                  </td>

                  <td className="sticky right-0 z-10 bg-white px-4 py-3 dark:bg-slate-900">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => onToggleDone(row)}
                        className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border transition ${
                          row.is_done
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                        }`}
                        title={row.is_done ? "Rouvir" : "Marquer terminé"}
                      >
                        <FaCheck />
                      </button>

                      <button
                        type="button"
                        onClick={() => onEdit(row)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 hover:text-indigo-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                        title="Modifier"
                      >
                        <FaEdit />
                      </button>

                      <button
                        type="button"
                        onClick={() => onDelete(row)}
                        disabled={deletingId === row.id}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 text-rose-700 transition hover:bg-rose-100 disabled:opacity-50 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
                        title="Supprimer"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}